import React from 'react';
import T from 'Components/Helpers/translate';

// Shared building blocks for the AI Workspace settings tabs (overview, usage,
// users, limits, promotions). Kept in one module so the per-tab components
// don't re-import each other just to reuse a credit formatter.

/** 1 credit = 1 SEK. Two decimals under 100, none above — matches the
 *  display convention the credit-pool dashboard uses everywhere. */
export const formatCredits = (creditsSek: number): string => {
	if (!Number.isFinite(creditsSek) || creditsSek <= 0) return '0';
	return creditsSek >= 100 ? Math.round(creditsSek).toLocaleString() : creditsSek.toFixed(2);
};

export type PoolMeter = {
	ceilingSek: number;
	remainingSek: number;
	/** The true figure, which can exceed 100 — a licensed account is billed for
	 *  overage rather than stopped, so "9,570 of 8,700" has to be able to say 110%
	 *  instead of contradicting the numerator printed beside it. */
	percent: number;
	/** `percent` clamped for a bar width, which cannot render past its track. */
	barPercent: number;
	level: 'ok' | 'warn' | 'over';
	isTrial: boolean;
};

/** What the credit meter measures against, and how far through it the account is.
 *
 *  `trialCapSek` is the cap the gate stops at on unpaid AI access (base's
 *  `resolveTrialCapSek`); on a licensed account it is null and the allocation is
 *  the figure. `poolApplies` is false on a rolling or unbounded period, where a
 *  monthly allocation and a period-scoped consumption measure different spans.
 *
 *  WEB-11391: the Overview tab and the rep Usage tab each carried their own copy
 *  of this arithmetic and had already drifted — one clamped the percentage and the
 *  other did not, and they disagreed on whether exactly-at-the-cap counts as spent.
 *  `>=` is the gate's own test: it refuses AT the cap. */
export const poolMeter = (
	pool: { allocatedSek: number; consumedSek: number; trialCapSek: number | null } | null | undefined,
	poolApplies = true
): PoolMeter => {
	const ceilingSek = pool ? pool.trialCapSek ?? pool.allocatedSek : 0;
	if (!pool || ceilingSek <= 0) {
		return { ceilingSek: 0, remainingSek: 0, percent: 0, barPercent: 0, level: 'ok', isTrial: false };
	}
	const fraction = pool.consumedSek / ceilingSek;
	const percent = Math.round(fraction * 100);
	return {
		ceilingSek,
		remainingSek: Math.max(0, ceilingSek - pool.consumedSek),
		percent,
		barPercent: Math.min(100, percent),
		level: !poolApplies ? 'ok' : fraction >= 1 ? 'over' : fraction >= 0.8 ? 'warn' : 'ok',
		isTrial: pool.trialCapSek != null
	};
};

/** Credit formatter for policy limits. Internal storage is still SEK
 *  (see `*Sek` fields), but the user-facing unit is "credits" — credits
 *  is the product abstraction that survives future pricing flexibility,
 *  while SEK is an implementation detail of today's pricing. Display
 *  whole numbers above 100, two decimals below. */
export const formatSek = (sek: number): string => {
	const unit = T('assistant.usageCreditsUnit') || 'credits';
	if (!Number.isFinite(sek) || sek <= 0) return `0 ${unit}`;
	const formatted = sek >= 100 ? Math.round(sek).toLocaleString() : sek.toFixed(2);
	return `${formatted} ${unit}`;
};

/** Segmented two-or-more-way toggle (You/Organisation, Org chart/Hire agents).
 *  Styled by `.ws-scope-toggle` in workspaceStyles.ts. */
export const ScopeToggle = <V extends string>({
	options,
	value,
	onChange,
	ariaLabel
}: {
	options: ReadonlyArray<{ value: V; label: string }>;
	value: V;
	onChange: (next: V) => void;
	ariaLabel: string;
}) => (
	<div className="ws-scope-toggle" role="tablist" aria-label={ariaLabel}>
		{options.map(opt => (
			<button
				key={opt.value}
				type="button"
				role="tab"
				aria-selected={value === opt.value}
				className={`ws-scope-toggle-btn ${value === opt.value ? 'is-active' : ''}`}
				onClick={() => onChange(opt.value)}
			>
				{opt.label}
			</button>
		))}
	</div>
);
